import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { State } from 'state';
import { Spinner, Timeline } from '../../../dashboard/components';
import { AuditLogEvent, AuditLogMessageAction, Message } from '../../../state/types';
import fetchMessageAuditLog from '../../../state/actions/fetch-message-audit-log';
import styles from './index.module.scss';
import UserMessageEvent from './message-events/user-message-event';
import AutomationMessageEvent from './message-events/automation-message-event';
import ManualMessageEvent from './message-events/manual-message-event';
import { AUTOMATION_USERS } from './audit-log-modal-constants';

export interface Props {
  message: Message;
}

type AuditLogColor = 'green' | 'red' | 'black';

export function getActionColor(eventType: AuditLogMessageAction): AuditLogColor {
  return eventType === AuditLogMessageAction.APPROVED
    ? 'green'
    : eventType === AuditLogMessageAction.REJECTED
    ? 'red'
    : 'black';
}

export const AuditLogModal: React.FC<Props> = ({ message }) => {
  const messageId: string = message.get('id');
  const userDisplayName = message.get('userDisplayName');
  const authorId = message.get('userId');
  const dispatch = useDispatch();
  const { timeline, users } = useSelector((state: State) => ({
    timeline: state.moderation.audit.auditLogs[messageId]?.timeline,
    users: state.moderation.users.users
  }));

  useEffect(() => {
    if (!timeline?.length) {
      dispatch(
        fetchMessageAuditLog({ messageId, userId: authorId, timestamp: message.get('postedAt') })
      );
    }
  }, []);

  const renderEvent = useCallback(
    (messageEvent: AuditLogEvent, index: number) => {
      const { userId, action } = messageEvent;
      const user = users[userId];
      const isUserAction = userId === authorId || action === AuditLogMessageAction.ARCHIVED;
      const isAutomatedAction = AUTOMATION_USERS.includes(userId);
      const isSpecialAction = action !== 'moderated';
      const key = `${index}_${userId}_${action}`;

      if (isUserAction) {
        return (
          <UserMessageEvent
            key={key}
            user={user}
            messageEvent={messageEvent}
            userDisplayName={userDisplayName}
          />
        );
      }

      if (isAutomatedAction) {
        return (
          <AutomationMessageEvent
            key={key}
            messageEvent={messageEvent}
            isSpecialAction={isSpecialAction}
            user={userId}
          />
        );
      }

      return (
        <ManualMessageEvent
          key={key}
          messageEvent={messageEvent}
          user={user}
          isSpecialAction={isSpecialAction}
        />
      );
    },
    [users, authorId, userDisplayName]
  );

  return (
    <div className={styles.auditLogModalContainer}>
      <h2 className={styles.auditLogTitle}>Message Log</h2>
      {timeline ? (
        <Timeline>
          {timeline.length ? (
            timeline.map(renderEvent)
          ) : (
            <div className={styles.emptyText}>
              Unfortunately, the history log of this message does not exist.
            </div>
          )}
        </Timeline>
      ) : (
        <Spinner size={'large'} />
      )}
    </div>
  );
};
